import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, get, onValue, off, update, push } from "firebase/database";
import { useNavigate } from "react-router-dom";

function SupportContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [supports, setSupports] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState({});
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const db = getDatabase();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
       //  console.log("No user logged in → redirect to login");
        navigate("/login", { replace: true });
        return;
      }

      try {
        const userRef = ref(db, `USERS/${user.uid}`);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
          // console.log("User not found in database → redirect to home");
          navigate("/", { replace: true });
          return;
        }

        const userData = snapshot.val();
        // console.log("Logged in user:", user.uid, userData);

        if (userData.USERISADMIN !== "Y") {
          // console.log("User is not admin → redirect to home");
          navigate("/", { replace: true });
          return;
        }

        // ✅ Admin verified
        setIsAdmin(true);
        setLoading(false);
        // console.log("Admin verified, can load support cases");

      } catch (error) {
        // console.error("Error verifying admin:", error);
        navigate("/login", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);


  useEffect(() => {
    if (!isAdmin) return;

    const db = getDatabase();
    const usersRef = ref(db, "USERS/"); // Fetch all users to get their support cases

    // console.log("Starting support listener...");

    onValue(
      usersRef,
      (snapshot) => {
        //console.log("Listener fired!");
        const usersData = snapshot.val();
        // console.log("Snapshot data:", usersData);

        if (!usersData) {
          setSupports([]);
          // console.log("No users found");
          return;
        }

        const formattedSupports = [];

        // Iterate through each user to collect their support cases
        Object.entries(usersData).forEach(([userId, userData]) => {
          if (userData.SUPPORT && typeof userData.SUPPORT === "object") {
            Object.entries(userData.SUPPORT).forEach(([supportId, supportData]) => {
              formattedSupports.push({
                id: supportId,
                userId,
                ...supportData,
              });
            });
          }
        });

        setSupports(formattedSupports);
        // console.log("Loaded support cases:", formattedSupports);
      },
      (error) => {
        // console.error("Listener error:", error);
      }
    );

    // Cleanup listener on unmount
    return () => off(usersRef, "value");
  }, [isAdmin]);

  const fetchTicketMessages = async (userId, ticketId) => {
    try {
      const database = getDatabase();
      const messagesRef = ref(database, `USERS/${userId}/SUPPORT/${ticketId}/MESSAGES`);
      
      const snapshot = await get(messagesRef);
      if (snapshot.exists()) {
        const messagesData = snapshot.val();
        const messagesArray = Object.entries(messagesData).map(([id, msg]) => ({
          id,
          ...msg
        })).sort((a, b) => a.createdAt - b.createdAt);
        
        setTicketMessages(prev => ({
          ...prev,
          [ticketId]: messagesArray
        }));
      } else {
        setTicketMessages(prev => ({
          ...prev,
          [ticketId]: []
        }));
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      setTicketMessages(prev => ({
        ...prev,
        [ticketId]: []
      }));
    }
  };

  const handleSendMessage = async (userId, ticketId) => {
    if (!messageText.trim()) return;

    setSendingMessage(true);
    try {
      const database = getDatabase();
      const messageRef = ref(database, `USERS/${userId}/SUPPORT/${ticketId}/MESSAGES`);
      
      const now = new Date();
      const newMessage = {
        MESSAGE_TEXT: messageText,
        SENT_BY: "ADMIN",
        SENT_DATE: now.toISOString(),
        createdAt: Date.now()
      };

      await push(messageRef, newMessage);
      setMessageText("");
      
      // Refresh messages
      await fetchTicketMessages(userId, ticketId);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleToggleTicketStatus = async (userId, ticketId, currentStatus) => {
    try {
      const database = getDatabase();
      const ticketRef = ref(database, `USERS/${userId}/SUPPORT/${ticketId}`);
      
      const newStatus = currentStatus === "Y" ? "N" : "Y";
      await update(ticketRef, {
        SUPPORT_ISOPEN: newStatus
      });

      // Refresh the supports list
      const usersRef = ref(database, "USERS/");
      const snapshot = await get(usersRef);
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const formattedSupports = [];

        Object.entries(usersData).forEach(([userId, userData]) => {
          if (userData.SUPPORT && typeof userData.SUPPORT === "object") {
            Object.entries(userData.SUPPORT).forEach(([supportId, supportData]) => {
              formattedSupports.push({
                id: supportId,
                userId,
                ...supportData,
              });
            });
          }
        });

        setSupports(formattedSupports);

        // Update selected ticket if it's the one we just updated
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(prev => ({
            ...prev,
            SUPPORT_ISOPEN: newStatus
          }));
        }
      }
    } catch (error) {
      console.error("Failed to toggle ticket status:", error);
    }
  };


  if (loading) {
    return <>Checking admin permissions...</>;
  }

  return (
    <div>
      <h1 className="mb-4">Admin Support Cases</h1>

      <div className="row g-3">
        <div className="col-12">
          <div className="mb-4">
            <p className="text-muted">Manage support tickets from all users</p>
          </div>

          {supports.length === 0 ? (
            <div className="card p-3">
              <p className="mb-0 text-gray-500">No support cases found.</p>
            </div>
          ) : (
            <div className="support-tickets-list">
              {supports.map((support) => {
                const isExpanded = selectedTicket?.id === support.id;
                return (
                  <div key={support.id} className="card mb-3 border-0 shadow-sm">
                    <div className="card-body">
                      {/* Ticket Header */}
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <h6 className="card-title mb-2 fw-bold">
                            {support.SUPPORT_TITLE || support.SUPPORT_REASON}
                          </h6>
                          <p className="mb-2 text-muted small">
                            #{support.id} <span className="event-badges">{support.SUPPORT_REASON}</span>
                          </p>
                          <p className="text-gray-500 mb-2 small">
                            <strong>User:</strong> {support.SUPPORT_USER}
                          </p>
                          <p className="text-gray-500 mb-0 small">
                            <strong>Request:</strong> {support.SUPPORT_DESCRIPTION}
                          </p>
                        </div>

                        <div className="col-md-4 text-md-end text-sm-start pt-3 pt-md-0">
                          <div className="d-flex justify-content-md-end justify-content-sm-start align-items-center gap-2">
                            <span className={`event-badges ${support.SUPPORT_ISOPEN === "Y" ? "open" : "resolved"}`}>
                              {support.SUPPORT_ISOPEN === "Y" ? <><i className="bi bi-check2-circle me-1 admin-badge"></i>Open</> : <><i className="bi bi-check2-circle me-1 verified-badge"></i>Resolved</>}
                            </span>
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                if (isExpanded) {
                                  setSelectedTicket(null);
                                } else {
                                  setSelectedTicket(support);
                                  fetchTicketMessages(support.userId, support.id);
                                }
                              }}
                            >
                              {isExpanded ? "Hide" : "Show"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Chat Section - Collapsible */}
                      {isExpanded && (
                        <div className="mt-4 pt-3 border-top">
                          {/* Messages Container */}
                          <div className="messages-container mb-3 p-3" style={{ backgroundColor: "var(--chat-bg)", borderRadius: "8px", maxHeight: "300px", overflowY: "auto", minHeight: "150px", border: "var(--chat-border)" }}>
                            {!ticketMessages[support.id] || ticketMessages[support.id].length === 0 ? (
                              <p className="text-center text-muted mb-0 small">No messages yet. Start the conversation!</p>
                            ) : (
                              ticketMessages[support.id].map((msg, idx) => {
                                const msgDate = msg.SENT_DATE ? new Date(msg.SENT_DATE) : null;
                                const formattedDateTime = msgDate ? `${msgDate.toLocaleDateString()} ${msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Unknown';
                                return (
                                  <div key={idx} className={`mb-2 ${msg.SENT_BY === "ADMIN" ? "text-end" : "text-start"}`}>
                                    <div style={{
                                      display: "inline-block",
                                      backgroundColor: msg.SENT_BY === "ADMIN" ? "var(--chat-message-admin-bg)" : "inherit",
                                      color: "var(--chat-message-text)",
                                      padding: "10px 14px",
                                      borderRadius: "12px",
                                      maxWidth: "75%",
                                      wordWrap: "break-word"
                                    }}>
                                      <p className="mb-1 small">{msg.MESSAGE_TEXT}</p>
                                      <small style={{ opacity: 0.7 }}>
                                        {msg.SENT_BY === "ADMIN" ? "Admin" : "User"} • {formattedDateTime}
                                      </small>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>

                          {/* Message Input - Only if ticket is open */}
                          {support.SUPPORT_ISOPEN === "Y" ? (
                            <div className="row g-2 mb-2">
                              <div className="col">
                                <textarea
                                  className="form-control form-control-sm"
                                  rows="2"
                                  placeholder="Type your response..."
                                  value={messageText}
                                  onChange={(e) => setMessageText(e.target.value)}
                                  disabled={sendingMessage}
                                  style={{ borderRadius: "8px" }}
                                />
                              </div>
                            </div>
                          ) : null}

                          <div className="row g-2">
                            <div className="col">
                              {support.SUPPORT_ISOPEN === "Y" && (
                                <button
                                  className="btn btn-sm btn-primary w-100"
                                  onClick={() => handleSendMessage(support.userId, support.id)}
                                  disabled={sendingMessage || !messageText.trim()}
                                  style={{ borderRadius: "8px" }}
                                >
                                  {sendingMessage ? "Sending..." : "Send Response"}
                                </button>
                              )}
                            </div>
                            <div className="col">
                              <div className="form-check form-switch pt-1">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`ticketSwitch${support.id}`}
                                  checked={support.SUPPORT_ISOPEN === "Y"}
                                  onChange={() =>
                                    handleToggleTicketStatus(
                                      support.userId,
                                      support.id,
                                      support.SUPPORT_ISOPEN
                                    )
                                  }
                                />
                                <label className="form-check-label" htmlFor={`ticketSwitch${support.id}`}>
                                  {support.SUPPORT_ISOPEN === "Y" ? "Ticket Open" : "Ticket Closed"}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupportContent;
